import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, message, Spin, Row, Col } from 'antd'
import MaterialIcon from './MaterialIcon'
import { gmailService } from '../services/gmailService'

interface CreateCustomerModalProps {
  visible: boolean
  threadId: string
  senderEmail?: string // Optional - for grouped conversations
  onClose: () => void
  onSuccess: (customer: any) => void
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  visible,
  threadId,
  senderEmail,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [extracting, setExtracting] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (visible && threadId) {
      extractCustomerData()
    }
  }, [visible, threadId])

  const extractCustomerData = async () => {
    try {
      setExtracting(true)
      const response = await gmailService.extractCustomer(threadId, senderEmail)
      
      if (response.success) {
        form.setFieldsValue(response.data)
        message.success('Customer data extracted successfully!')
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to extract customer data')
    } finally {
      setExtracting(false)
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setCreating(true)
      
      const response = await gmailService.createCustomer(threadId, values)
      
      if (response.success) {
        message.success(response.message || 'Customer saved successfully!')
        onSuccess(response.data)
        form.resetFields()
        onClose()
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create customer')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      title={
        <span>
          <MaterialIcon icon="person_add" style={{ marginRight: '8px' }} />
          Create Customer from Email
        </span>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="extract"
          icon={<MaterialIcon icon="auto_awesome" />}
          onClick={extractCustomerData}
          loading={extracting}
          disabled={creating}
        >
          Re-extract
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={creating}
          disabled={extracting}
        >
          Create Customer
        </Button>,
      ]}
    >
      {extracting ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <MaterialIcon icon="auto_awesome" style={{ fontSize: '24px' }} />
            <div>AI is extracting customer data from email...</div>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          disabled={creating}
        >
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input prefix={<MaterialIcon icon="person" />} />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input prefix={<MaterialIcon icon="person" />} />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input prefix={<MaterialIcon icon="email" />} />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
          >
            <Input prefix={<MaterialIcon icon="phone" />} />
          </Form.Item>

          <Form.Item
            label="Street Address"
            name="address"
          >
            <Input prefix={<MaterialIcon icon="home" />} placeholder="123 Main St, Apt 4" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="City"
                name="city"
              >
                <Input prefix={<MaterialIcon icon="location_city" />} placeholder="Los Angeles" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="State"
                name="state"
              >
                <Input prefix={<MaterialIcon icon="map" />} placeholder="CA" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="ZIP"
                name="zipCode"
              >
                <Input prefix={<MaterialIcon icon="pin_drop" />} placeholder="90210" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Company"
            name="company"
          >
            <Input prefix={<MaterialIcon icon="business" />} />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}

export default CreateCustomerModal

